import {Injectable} from "@angular/core";
import {nanoid} from "nanoid";
import {environment} from "@environments/environment";
import {Uppy, UppyFile} from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import Dashboard from "@uppy/dashboard/lib/Dashboard";
import Webcam from "@uppy/webcam/lib/Webcam";
import {BehaviorSubject} from "rxjs";
import {TranslocoService} from "@ngneat/transloco";
import XHRUpload from "@uppy/xhr-upload";

export interface IAudioUpload {
  url: string,
  transcription?: string
}

@Injectable({
  providedIn: 'root'
})
export class AudioUploadService {
  private maxFileSize = 50 * Math.pow(1024, 2);
  private maxNumberOfFiles = 1;
  private minNumberOfFiles = 1;

  uploadedAudio$: Record<string, BehaviorSubject<IAudioUpload | null>> = {};
  isUploading$: Record<string, BehaviorSubject<boolean>> = {};
  private uppyInstances: Record<string, Uppy> = {};

  constructor() {
  }

  /**
   * Convertit un code de langue simple (fr, en, nl, it) en code de langue GCP (fr-FR, en-US, nl-NL, it-IT)
   */
  private convertToGcpLanguageCode(lang: string): string {
    const languageMap: Record<string, string> = {
      'fr': 'fr-FR',
      'en': 'en-US',
      'nl': 'nl-NL',
      'it': 'it-IT',
      'es': 'es-ES',
      'de': 'de-DE',
      'pt': 'pt-PT',
    };
    return languageMap[lang?.toLowerCase()] || 'fr-FR'; // Par défaut fr-FR si la langue n'est pas reconnue
  }

  uploadAudioFile(audioBlob: Blob, questionKey: string, surveyId: string, sourceLanguage: string = 'fr') {
    this.isUploading$[questionKey] ??= new BehaviorSubject<boolean>(null);
    this.isUploading$[questionKey].next(true);
    const uppy = new Uppy({
      id: nanoid(),
      restrictions: {
        allowedFileTypes: ['audio/*'],
        maxFileSize: this.maxFileSize,
        maxNumberOfFiles: this.maxNumberOfFiles,
        minNumberOfFiles: this.minNumberOfFiles,
      },
      autoProceed: true
    })
      .use(Transloadit, {
        assemblyOptions: {
          params: {
            auth: {key: environment.medias.transloadIt_key},
            fields: {surveyId},
            steps: {
              ':original': {
                robot: '/upload/handle'
              },
              viruscheck: {
                use: ':original',
                robot: '/file/virusscan',
                error_on_decline: true
              },
              audio_file: {
                use: ['viruscheck'],
                robot: '/file/filter',
                result: true,
                accepts: [['${file.mime}', 'regex', 'audio']],
                error_on_decline: false
              },
              convert_audio: {
                use: ['audio_file'],
                robot: '/audio/encode',
                preset: 'mp3',
                ffmpeg_stack: 'v4.3.1'
              },
              'transcribed-aws': {
                use: ':original',
                robot: '/speech/transcribe',
                provider: 'gcp',
                granularity: 'list',
                format: 'json',
                source_language: this.convertToGcpLanguageCode(sourceLanguage),
                result: true
              },
              exportTranscription: {
                use: 'transcribed-aws',
                robot: '/s3/store',
                credentials: 'S3 Approbation',
                bucket: `inbe-${environment.production ? 'production' : 'approbation'}`,
                path: 'responses/${fields.surveyId}/${file.id}-transcription.json',
                url_prefix: `https://inbe-${environment.production ? 'production' : 'approbation'}.s3.us-west-2.amazonaws.com/`,
                acl: 'public-read',
                sign_urls_for: -1,
                result: true
              },
              exportUpload: {
                use: ['convert_audio'],
                bundle_steps: true,
                robot: '/s3/store',
                credentials: 'S3 Approbation',
                bucket: `inbe-${environment.production ? 'production' : 'approbation'}`,
                path: 'responses/${fields.surveyId}/${file.id}.mp3',
                url_prefix: `https://inbe-${environment.production ? 'production' : 'approbation'}.s3.us-west-2.amazonaws.com/`,
                acl: 'public-read',
                sign_urls_for: -1
              }
            }
          },
        },

        waitForEncoding: true,
        waitForMetadata: true,
      })
      .on('complete', async (result) => {
        // Vérifier que l'upload n'a pas été annulé avant de traiter le résultat
        if (this.uppyInstances[questionKey] !== uppy) {
          // L'upload a été annulé, ignorer le résultat
          return;
        }

        const transloaditResult = result.transloadit;

        // Vérifier que transloaditResult existe et contient des résultats
        if (!transloaditResult || !Array.isArray(transloaditResult) || transloaditResult.length === 0) {
          console.error('❌ Transloadit result is empty or invalid');
          this.isUploading$[questionKey].next(false);
          delete this.uppyInstances[questionKey];
          return;
        }

        let transcription: string | null = null;

        // 1) Essayer via exportTranscription (S3) si disponible
        const exportedTranscription = transloaditResult[0]?.results?.exportTranscription?.[0];

        if (exportedTranscription?.ssl_url) {
          try {
            const response = await fetch(exportedTranscription.ssl_url);
            const json = await response.json();
            transcription = json?.text ?? json?.data?.text ?? null;
          } catch (err) {
            console.error('❌ Erreur en téléchargeant la transcription depuis S3', err);
          }
        }

        // 2) Fallback : utilisation du proxy backend pour contourner CORS
        if (!transcription) {
          const transcribedResult = transloaditResult[0]?.results?.['transcribed-aws']?.[0];

          if (transcribedResult?.ssl_url) {
            const proxyUrl = `${environment.apiUrl}/proxy-transcription?url=${encodeURIComponent(transcribedResult.ssl_url)}`;

            try {
              const response = await fetch(proxyUrl);
              const json = await response.json();
              transcription = json?.text ?? json?.data?.text ?? null;
            } catch (err) {
              console.error('❌ Erreur lors du téléchargement via proxy', err);

              // 3) Dernier recours : tentative directe (peut échouer à cause de CORS)
              try {
                const directResponse = await fetch(transcribedResult.ssl_url);
                const directJson = await directResponse.json();
                transcription = directJson?.text ?? null;
              } catch (err2) {
                console.error('❌ CORS toujours bloqué (fallback direct)', err2);
              }
            }
          }
        }

        const audioUrl = transloaditResult[0]?.results?.convert_audio?.[0]?.ssl_url;
        if (audioUrl) {
          this.uploadedAudio$[questionKey] ??= new BehaviorSubject<IAudioUpload | null>(null);
          this.uploadedAudio$[questionKey].next({url: audioUrl, transcription});
        }
        this.isUploading$[questionKey].next(false);
        // Nettoyer l'instance après succès
        delete this.uppyInstances[questionKey];
      })
      .on('error', (err) => {
        console.error('❌ Erreur avec Transloadit', err);
        // Ne pas afficher l'alerte si l'upload a été annulé
        if (this.uppyInstances[questionKey] === uppy) {
          alert('Erreur d\'upload ou de traitement');
        }
        this.isUploading$[questionKey].next(false);
        delete this.uppyInstances[questionKey];
      });

    // Stocker l'instance Uppy pour pouvoir l'annuler plus tard
    this.uppyInstances[questionKey] = uppy;

    uppy.addFile({
      name: `audio.webm`,
      type: audioBlob.type,
      data: audioBlob
    });

  }

  cancelUpload(questionKey: string): void {
    const uppy = this.uppyInstances[questionKey];
    if (uppy) {
      try {
        // Annuler tous les fichiers en cours d'upload
        uppy.cancelAll();
      } catch (error) {
        console.error('Error cancelling upload:', error);
      }
      // Nettoyer la référence et mettre à jour l'état
      delete this.uppyInstances[questionKey];
      this.isUploading$[questionKey]?.next(false);
    }
  }

  deleteUpload(questionKey: string) {
    this.uploadedAudio$[questionKey].next(null);
  }

  /**
   * Vérifie s'il y a des uploads en cours pour une question spécifique
   */
  isUploading(questionKey: string): boolean {
    return this.isUploading$[questionKey]?.value === true;
  }

  /**
   * Vérifie s'il y a des uploads en cours pour n'importe quelle question
   */
  hasAnyUploadInProgress(): boolean {
    return Object.values(this.isUploading$).some(subject => subject?.value === true);
  }

  /**
   * Récupère toutes les questions qui ont des uploads en cours
   */
  getQuestionsWithUploadInProgress(): string[] {
    return Object.entries(this.isUploading$)
      .filter(([_, subject]) => subject?.value === true)
      .map(([questionKey, _]) => questionKey);
  }
}
